/**
 * Scene Analysis API Service
 * Handles communication with backend analysis endpoints
 */

import axios from 'axios';
import type {
  StartAnalysisRequest,
  StartAnalysisResponse,
  AnalysisResponse,
  AnalysisResults,
  AudioSegmentInput,
} from '../../shared/types/analysis';

export interface AudioSegmentForUpload {
  blob?: Blob; // Either blob or blobUrl must be provided
  blobUrl?: string; // If blobUrl is provided, blob will be fetched from it
  segmentIndex: number;
  tsStart: number;
  tsEnd: number | null;
  hasMic: boolean;
  hasSystemAudio: boolean;
  isMuted: boolean;
}

// Use relative path - will be proxied to backend server
const API_BASE_URL = '';

export class SceneAnalysisService {
  /**
   * Start analysis for a scene
   */
  static async startAnalysis(
    sceneId: number,
    videoUrl?: string,
    micEnabled?: boolean,
    options?: StartAnalysisRequest
  ): Promise<StartAnalysisResponse> {
    const response = await axios.post<StartAnalysisResponse>(
      `${API_BASE_URL}/api/scene/${sceneId}/analyze`,
      { ...options, videoUrl, micEnabled }, // Include videoUrl and micEnabled in request body
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  /**
   * Get analysis results or status
   */
  static async getAnalysis(sceneId: number): Promise<AnalysisResponse> {
    const response = await axios.get<AnalysisResponse>(
      `${API_BASE_URL}/api/scene/${sceneId}/analysis`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  }

  /**
   * Poll for analysis results until complete or timeout
   * 
   * @param sceneId - Scene ID
   * @param onProgress - Progress callback
   * @param timeout - Timeout in milliseconds (default: 2 minutes)
   * @returns Analysis results
   */
  static async pollUntilComplete(
    sceneId: number,
    onProgress?: (response: AnalysisResponse) => void,
    timeout: number = 120000
  ): Promise<AnalysisResults> {
    const startTime = Date.now();
    const pollInterval = 2000; // Poll every 2 seconds

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          // Check timeout
          if (Date.now() - startTime > timeout) {
            reject(new Error('Analysis polling timeout'));
            return;
          }

          // Get current status
          const response = await this.getAnalysis(sceneId);

          // Call progress callback
          if (onProgress) {
            onProgress(response);
          }

          // Check status
          if (response.status === 'ready') {
            // Analysis complete
            resolve(response.results);
          } else if (response.status === 'failed') {
            // Analysis failed
            reject(new Error(response.error.message || 'Analysis failed'));
          } else {
            // Still processing, poll again
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };

      // Start polling
      poll();
    });
  }

  /**
   * Check if analysis is available for a scene
   * (Mic must be enabled and scene must have video)
   */
  static async isAnalysisAvailable(sceneId: number): Promise<boolean> {
    try {
      const response = await this.getAnalysis(sceneId);
      
      // Check if mic was enabled
      if (response.status === 'pending' && 'micEnabled' in response) {
        return response.micEnabled;
      }

      // If not pending, analysis is available
      return true;
    } catch (error) {
      console.error('[SceneAnalysisService] Error checking availability:', error);
      return false;
    }
  }

  /**
   * Upload audio segments temporarily for analysis (unconfirmed recordings)
   * Returns the uploaded segment info with S3 URLs
   */
  static async uploadTempAudioSegments(
    sceneId: number,
    segments: AudioSegmentForUpload[]
  ): Promise<AudioSegmentInput[]> {
    const formData = new FormData();

    // Resolve blobs (either use provided blob or fetch from blobUrl)
    const resolvedBlobs = await Promise.all(
      segments.map(async (segment) => {
        if (segment.blob && segment.blob.size > 0) {
          return segment.blob;
        } else if (segment.blobUrl) {
          // Fetch blob from blob URL
          try {
            const response = await fetch(segment.blobUrl);
            return await response.blob();
          } catch (error) {
            console.error(`[SceneAnalysisService] Failed to fetch blob from URL for segment ${segment.segmentIndex}:`, error);
            return null;
          }
        }
        return null;
      })
    );

    // Filter out segments with no valid blob
    const validSegments = segments.filter((_, index) => resolvedBlobs[index] !== null);
    const validBlobs = resolvedBlobs.filter((blob) => blob !== null) as Blob[];

    if (validBlobs.length === 0) {
      throw new Error('No valid audio segments to upload');
    }

    // Add each audio blob as a file
    validSegments.forEach((segment, index) => {
      const blob = validBlobs[index];
      const filename = `audio-segment-${segment.segmentIndex}.webm`;
      formData.append('audioSegments', blob, filename);
    });

    // Add metadata as JSON (only for valid segments)
    const metadata = validSegments.map((segment) => ({
      segmentIndex: segment.segmentIndex,
      tsStart: segment.tsStart,
      tsEnd: segment.tsEnd,
      hasMic: segment.hasMic,
      hasSystemAudio: segment.hasSystemAudio,
      isMuted: segment.isMuted,
    }));
    formData.append('metadata', JSON.stringify(metadata));

    const response = await axios.post<{ success: boolean; segments: AudioSegmentInput[] }>(
      `${API_BASE_URL}/api/scene/${sceneId}/upload-temp-audio`,
      formData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    console.log(`[SceneAnalysisService] Uploaded ${response.data.segments.length} temp audio segments for scene ${sceneId}`);
    return response.data.segments;
  }

  /**
   * Clean up temporary audio segments for a scene
   * Called when recording is discarded or re-recorded
   */
  static async cleanupTempAudio(sceneId: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/api/scene/${sceneId}/temp-audio`, {
        withCredentials: true,
      });
      console.log(`[SceneAnalysisService] Cleaned up temp audio for scene ${sceneId}`);
    } catch (error) {
      console.warn('[SceneAnalysisService] Error cleaning up temp audio:', error);
      // Don't throw - cleanup is non-critical
    }
  }
}

export default SceneAnalysisService;
